import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Typography } from "@material-ui/core";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import React from "react";
import { Controller, useForm } from "react-hook-form";
import { useHistory } from "react-router-dom";

import { AuthErrorSummary, AuthNote, AuthStatusMark } from "@/components/AuthFeedback";
import { CustomButton } from "@/components/Button";
import { CustomForm } from "@/components/Form";
import { TextLink } from "@/components/TextLink";
import { useSendPasswordResetLink } from "@/hooks/useResetPassword";
import { EntranceActions, EntranceTemplate } from "@/layouts/EntranceTemplate";
import { sendPasswordResetLinkSchema, SendPasswordResetLinkFormData } from "@/validations/passwordReset";

export const PasswordResetSendLinkPage: React.FC = () => {
  const { sendPasswordResetLink, loading, error, sentEmail } = useSendPasswordResetLink();
  const history = useHistory();
  const {
    handleSubmit,
    formState: { errors },
    control,
  } = useForm<SendPasswordResetLinkFormData>({
    resolver: zodResolver(sendPasswordResetLinkSchema),
    mode: "onTouched",
    shouldFocusError: true,
    defaultValues: { email: "" },
  });
  if (sentEmail !== null)
    return (
      <EntranceTemplate
        title="再設定のリンクを送信しました"
        subTitle={
          <>
            メールのリンクを開いて、
            <br />
            新しいパスワードを設定してください。
          </>
        }
        mark={<AuthStatusMark />}
      >
        <AuthNote>
          <Typography style={{ fontSize: 12, marginBottom: 4 }}>送り先</Typography>
          <Typography style={{ fontSize: 14, overflowWrap: "anywhere" }} color="textPrimary">
            {sentEmail}
          </Typography>
        </AuthNote>
        <Box mt={3}>
          <AuthNote title="届かないときは">
            <ul style={{ paddingLeft: 16, margin: 0, display: "grid", gap: 8 }}>
              <li>迷惑メールのフォルダをご確認ください。</li>
              <li>メールアドレスの打ち間違いなどがないか確かめて、もう一度お試しください。</li>
              <li>リンクは送信から60分で使えなくなります。</li>
            </ul>
          </AuthNote>
        </Box>
        <EntranceActions>
          <CustomButton buttonSize="L" tone="outline" fullWidth onClick={() => history.push("/login")}>
            ログインに戻る
          </CustomButton>
        </EntranceActions>
      </EntranceTemplate>
    );
  return (
    <EntranceTemplate
      title="パスワードを再設定します"
      subTitle={
        <>
          登録しているメールアドレスに、
          <br />
          再設定のリンクをお送りします。
        </>
      }
    >
      <form onSubmit={handleSubmit(sendPasswordResetLink)} noValidate>
        <Box display="flex" flexDirection="column" style={{ gap: 16 }}>
          <AuthNote>
            <Box display="flex" style={{ gap: 8 }}>
              <InfoOutlinedIcon style={{ fontSize: 20, flexShrink: 0 }} />
              <span>
                Apple や Google で登録した方には、パスワードがありません。これまでと同じ方法でログインしてください。
              </span>
            </Box>
          </AuthNote>
          <Controller
            name="email"
            control={control}
            render={({ ...field }) => (
              <CustomForm
                {...field}
                label="メールアドレス"
                inputMode="email"
                autoComplete="email"
                disabled={loading}
                error={!!errors.email}
                errorText={errors.email?.message}
                placeHolder="propally@example.com"
              />
            )}
          />
        </Box>
        <EntranceActions>
          <AuthErrorSummary message={error} />
          <CustomButton type="submit" buttonSize="L" fullWidth disabled={loading}>
            再設定のリンクを送る
          </CustomButton>
          <Box mt={0.5}>
            <CustomButton type="button" tone="text" onClick={() => history.push("/login")}>
              ログインに戻る
            </CustomButton>
          </Box>
        </EntranceActions>
      </form>
      <Box mt={3} textAlign="center">
        <Typography style={{ fontSize: 14 }}>
          何か問題が発生している場合は
          <br />
          <TextLink href="https://form.run/@propally-1619963249" targetBlank underline>
            こちらからご報告ください
          </TextLink>
        </Typography>
      </Box>
    </EntranceTemplate>
  );
};
